import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import DuplicateDealMessage from '../../../../components/DuplicateDealMessage';
import { useLEAccordionItemContext } from '../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import VehicleInfo from '../../../../components/VehicleInfo';
import { Input, Label, Select } from '../../../../components/formComponents';
import {
  LICENSE_PLATE_NAME,
  VIN_NAME,
} from '../../../../components/licensePlateOrVinInput/LicensePlateOrVinInput';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import { statesArray } from '../../../../constants/states';
import {
  KbbVinResult,
  Maybe,
  useTemporaryInfoUpdateMutation,
} from '../../../../gql/generated/graphql';
import { CookieKeys, useCookie } from '../../../../hooks/useCookie';
import useDecodeCar from '../../../../hooks/useDecodeCar';
import { stateValidation } from '../../../../utils/validation/address';
import validateLicenseNumber, {
  INVALID_LICENSE_PLATE_MSG,
  INVALID_VIN_MSG,
} from '../../../../utils/validation/licenseNumberValidator';
import validateVin from '../../../../utils/validation/vinValidator';

type CarInfo = {
  license_plate_number: string;
  license_plate_state: string;
  vin: string;
  make: string;
  model: string;
  kbb_trim_name: string;
  fuel_type: string;
  vehicle_type: string;
};

const carInfoInitialValues: CarInfo = {
  license_plate_number: '',
  license_plate_state: '',
  vin: '',
  make: '',
  model: '',
  kbb_trim_name: '',
  fuel_type: '',
  vehicle_type: '',
};

interface ManualCarEntryPanelProps {
  onReturnToVehicleList?: () => void;
  preselectedManualCar?: { vin: string; trimName: string };
}

const ManualCarEntryPanel = ({
  onReturnToVehicleList,
  preselectedManualCar,
}: ManualCarEntryPanelProps) => {
  const { updateState, index, setOpenAccordionIndex } = useLEAccordionItemContext();

  const [currentInputType, setCurrentInputType] = useState<string>(LICENSE_PLATE_NAME);
  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const isLicensePlate = LICENSE_PLATE_NAME === currentInputType;

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const {
    fetchedCarDetails,
    setFetchedCarDetails,
    kbbOptions,
    setKbbOptions,
    decodingLoading,
    dealExists,
    flowStartError,
    vinData,
    handleDecodeCar,
  } = useDecodeCar();

  const formikRef = useRef<FormikProps<CarInfo>>(null);
  const hasDecodedPreselected = useRef(false);

  useEffect(() => {
    if (preselectedManualCar?.vin && formikRef.current && !hasDecodedPreselected.current) {
      hasDecodedPreselected.current = true;
      setCurrentInputType(VIN_NAME);
      const formik = formikRef.current;
      formik.setFieldValue('vin', preselectedManualCar.vin);

      const values = { ...formik.values, vin: preselectedManualCar.vin };
      handleDecodeCar(
        { values, setFieldError: formik.setFieldError, setValues: formik.setValues },
        false,
      );
    }
  }, [preselectedManualCar, handleDecodeCar]);

  useEffect(() => {
    if (
      preselectedManualCar?.trimName &&
      kbbOptions.length > 0 &&
      formikRef.current &&
      formikRef.current.values.vin === preselectedManualCar.vin &&
      !formikRef.current.values.kbb_trim_name
    ) {
      const matchingOption = kbbOptions.find((opt) => opt.value === preselectedManualCar.trimName);
      if (matchingOption) {
        formikRef.current.setFieldValue('kbb_trim_name', preselectedManualCar.trimName);
      }
    }
  }, [preselectedManualCar, kbbOptions]);

  const handleSubmit = async (values: CarInfo) => {
    const vehicle: Maybe<KbbVinResult> = vinData?.kbbVin?.vinResults?.find(
      (vinResult) => vinResult?.trimName === values.kbb_trim_name,
    );

    await updateTemporaryInfo({
      variables: {
        info: {
          id: guid,
          data: {
            kbb_vehicle_id: vehicle?.vehicleId,
            kbb_vehicle_name: vehicle?.vehicleName,
            kbb_trim_name: vehicle?.trimName,
            kbb_all_options: vehicle?.vehicleOptions,
          },
        },
      },
    });

    updateState({ status: 'complete' });
    setOpenAccordionIndex(index + 1);
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={carInfoInitialValues}
      validationSchema={Yup.object({
        ...(isLicensePlate
          ? {
              license_plate_number: Yup.string()
                .required('Please enter a value')
                .test('is-valid-license-number', INVALID_LICENSE_PLATE_MSG, async (value) =>
                  validateLicenseNumber({ licenseNumber: value }),
                ),
              license_plate_state: stateValidation,
            }
          : {
              vin: Yup.string()
                .required('Please enter a value')
                .test('is-valid-vin', INVALID_VIN_MSG, (value) => validateVin(value)),
            }),
        make: Yup.string().required('Please enter a value'),
        model: Yup.string().required('Please enter a value'),
        kbb_trim_name: Yup.string().required('Please select a trim'),
      })}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      onReset={() => {
        setFetchedCarDetails(undefined);
        setKbbOptions([]);
      }}
    >
      {({ isSubmitting, resetForm, touched, errors, values, setFieldError, setValues }) => (
        <Form>
          <Label>VEHICLE INFORMATION</Label>
          {!fetchedCarDetails && (
            <Flex flexDir="column">
              <Flex w="full" gap={1} flexDir={{ base: 'column', md: 'row' }}>
                {isLicensePlate ? (
                  <Flex w={{ md: '70%' }} gap={1}>
                    <Input
                      _container={{ w: '70%' }}
                      name="license_plate_number"
                      placeholder="License Plate"
                      onChange={() => setFetchedCarDetails(undefined)}
                      invalid={
                        dealExists ||
                        !!flowStartError ||
                        (touched.license_plate_number && !!errors.license_plate_number)
                      }
                    />
                    <Select
                      _container={{ w: '30%' }}
                      name="license_plate_state"
                      placeholder="State"
                      options={statesArray}
                      onChange={() => setFetchedCarDetails(undefined)}
                      invalid={
                        dealExists || (touched.license_plate_state && !!errors.license_plate_state)
                      }
                    />
                  </Flex>
                ) : (
                  <Input
                    _container={{ w: { md: '70%' } }}
                    onChange={() => setFetchedCarDetails(undefined)}
                    name="vin"
                    placeholder="Your vehicle's 17-digit VIN"
                    invalid={dealExists || !!flowStartError || (touched.vin && !!errors.vin)}
                  />
                )}
                <PrimaryButton
                  onClick={async () =>
                    handleDecodeCar({ values, setFieldError, setValues }, isLicensePlate, () =>
                      updateState({ status: 'none' }),
                    )
                  }
                  mt={{ base: 2, md: 0 }}
                  size="sm"
                  h="35px"
                  variant="secondary"
                  fontSize="sm"
                  w={{ base: '50%', md: '30%' }}
                  isLoading={decodingLoading}
                >
                  FIND MY CAR
                </PrimaryButton>
              </Flex>
              <Button
                variant="link"
                mt={2}
                onClick={() => {
                  setCurrentInputType(isLicensePlate ? VIN_NAME : LICENSE_PLATE_NAME);
                  resetForm();
                }}
                textAlign="left"
                maxW="fit-content"
              >
                {isLicensePlate ? 'Use VIN' : 'Use License Plate'}
              </Button>
              {dealExists && (
                <Box mt={1}>
                  <DuplicateDealMessage textAlign="left" />
                </Box>
              )}
              {flowStartError && !dealExists && (
                <Box textAlign="left" mt={1}>
                  <Text color="red.500" fontWeight="semibold" wordBreak="break-word">
                    {flowStartError}
                  </Text>
                </Box>
              )}
            </Flex>
          )}
          {(fetchedCarDetails || decodingLoading) && (
            <>
              <Flex gap={4} mt={2} mb={2} flexDir="column">
                <VehicleInfo
                  year={fetchedCarDetails?.year ?? ''}
                  make={fetchedCarDetails?.make ?? ''}
                  model={fetchedCarDetails?.model ?? ''}
                  vin={fetchedCarDetails?.vin ?? ''}
                  infoLoading={decodingLoading}
                  p={2}
                  w="100%"
                  hideVin
                />
                <Box w="100%" mb="2px" mt="auto">
                  <Select
                    name="kbb_trim_name"
                    placeholder="Select Trim"
                    alignSelf="self-start"
                    w="full"
                    options={kbbOptions}
                    isDisabled={kbbOptions.length <= 1}
                  />
                </Box>
              </Flex>
              <Button
                variant="link"
                mb={4}
                onClick={() => resetForm()}
                fontWeight={600}
                textAlign="left"
                maxW="fit-content"
              >
                This is not my car
              </Button>
            </>
          )}

          <VStack>
            <PrimaryButton mt="30px" type="submit" isLoading={isSubmitting}>
              NEXT
            </PrimaryButton>
            {onReturnToVehicleList && (
              <Button mt={3} onClick={onReturnToVehicleList} variant="link" textAlign="center">
                Return to vehicle list
              </Button>
            )}
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

export default ManualCarEntryPanel;
